import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EmailIcon from "@mui/icons-material/Email";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import moment from "moment";
import { motion } from "framer-motion";
import api from "../../../utils/api";

const WorkOrderTimeline = ({ workOrderId }) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const response = await api.get(`/externalWorkOrderTimeline/${workOrderId}`);
        setTimelineData(response.data);
      } catch (error) {
        console.error("Error fetching timeline data:", error);
        setError("Failed to load timeline data.");
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [workOrderId]);

  const combinedTimeline = useMemo(() => {
    if (!timelineData || !timelineData.workOrder) return [];

    const { workOrder, comments = [], events = [], emails = [] } = timelineData;

    const timeline = [
      {
        type: "workOrder",
        title: "Work Order Created",
        time: workOrder.created_at,
      },
      ...comments.map(({ comment_text, sent_at }) => ({
        type: "comment",
        title: comment_text,
        time: sent_at || new Date().toISOString(),
      })),
      ...events.map(({ title, created_at }) => ({
        type: "event",
        title,
        time: created_at || new Date().toISOString(),
      })),
      ...emails.map(({ subject, sent_at }) => ({
        type: "email",
        title: subject,
        time: sent_at || new Date().toISOString(),
      })),
      ...(workOrder.resolved_at
        ? [
            {
              type: "workOrder",
              title: "Work Order Resolved",
              time: workOrder.resolved_at,
            },
          ]
        : []),
    ];

    return timeline.sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [timelineData]);

  if (loading) return <Typography>Loading timeline...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ padding: 2 }}>
      <Typography
        variant="h6"
        sx={{ mb: 3, fontWeight: "bold", color: "#333" }}
      >
        Work Order Timeline
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          paddingLeft: 3,
          borderLeft: "2px solid #ccc",
        }}
      >
        {combinedTimeline.map(({ type, title, time }, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card
              sx={{
                position: "relative",
                mb: 2,
                padding: 1.5,
                boxShadow: 1,
                borderRadius: 2,
                maxWidth: "80%",
                bgcolor: "#fff",
                border: "1px solid #ddd",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: 3,
                  transform: "scale(1.02)",
                },
              }}
            >
              <CircleIcon
                sx={{
                  color: {
                    workOrder: "#1E88E5",
                    comment: "#43A047",
                    event: "#8E24AA",
                    email: "#FB8C00",
                  }[type] || "#757575",
                  fontSize: 14,
                  position: "absolute",
                  left: "-25px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />

              <CardContent sx={{ padding: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  {type === "workOrder" && (
                    <AssignmentTurnedInIcon
                      sx={{ color: "#1E88E5", mr: 1, fontSize: 20 }}
                    />
                  )}
                  {type === "comment" && (
                    <ChatBubbleIcon
                      sx={{ color: "#43A047", mr: 1, fontSize: 20 }}
                    />
                  )}
                  {type === "event" && (
                    <EventNoteIcon
                      sx={{ color: "#8E24AA", mr: 1, fontSize: 20 }}
                    />
                  )}
                  {type === "email" && (
                    <EmailIcon
                      sx={{ color: "#FB8C00", mr: 1, fontSize: 20 }}
                    />
                  )}

                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", color: "#333" }}
                  >
                    {title}
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "#555",
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 12, mr: 0.5 }} />
                  {moment(time).format("YYYY-MM-DD HH:mm:ss")}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>

      <Legend />
    </Box>
  );
};

const Legend = () => (
  <Box sx={{ mt: 3 }}>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {[
        {
          color: "#1E88E5",
          icon: (
            <AssignmentTurnedInIcon
              sx={{ fontSize: 14, mr: 1, color: "#1E88E5" }}
            />
          ),
          label: "Work Order Created/Resolved",
        },
        {
          color: "#43A047",
          icon: (
            <ChatBubbleIcon
              sx={{ fontSize: 14, mr: 1, color: "#43A047" }}
            />
          ),
          label: "Comments",
        },
        {
          color: "#8E24AA",
          icon: (
            <EventNoteIcon
              sx={{ fontSize: 14, mr: 1, color: "#8E24AA" }}
            />
          ),
          label: "Events",
        },
        {
          color: "#FB8C00",
          icon: (
            <EmailIcon
              sx={{ fontSize: 14, mr: 1, color: "#FB8C00" }}
            />
          ),
          label: "Email Logs",
        },
      ].map(({ color, icon, label }, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
          {icon}
          <Typography variant="body2" sx={{ color }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export default WorkOrderTimeline;